// ============================================================================
// MÓDULO 7 - UNIDAD 8: COMPARING PLACES
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Comparative & superlative modifiers + "one of" structure
// Modifiers: much, a lot, slightly, a bit, easily, by far
// Critical: "One of the tallest buildings" (plural!)
// ============================================================================

export const M7_UNIT_8 = {
  id: "m7-u8",
  title: "Unit 8: Comparing Places",
  topic: "Master natural comparisons with modifiers! Learn to show big differences (much/a lot), small differences (slightly/a bit), and emphasize superlatives (easily/by far)!",
  
  grammarImage: "/images/grammar/m7-u8-comparison-modifiers.jpg",
  grammarText: `
    <h3>Comparing Places - Modifiers & "One Of" Structure</h3>
    
    <div class="comparative-modifiers">
      <h4>Comparative Modifiers (Show How Different)</h4>
      
      <div class="big-difference">
        <h5>BIG Differences - MUCH / A LOT:</h5>
        <ul>
          <li>Paris is <strong>much bigger</strong> than Lyon</li>
          <li>Dubai is <strong>a lot hotter</strong> than London</li>
          <li>Tokyo is <strong>much more expensive</strong> than Bangkok</li>
        </ul>
        <p>Use: When the difference is significant and obvious</p>
      </div>
      
      <div class="small-difference">
        <h5>SMALL Differences - SLIGHTLY / A BIT / A LITTLE:</h5>
        <ul>
          <li>London is <strong>slightly larger</strong> than Berlin</li>
          <li>This building is <strong>a bit older</strong> than that one</li>
          <li>Rome is <strong>a little more touristy</strong> than Milan</li>
        </ul>
        <p>Use: When the difference is noticeable but not huge</p>
      </div>
    </div>

    <div class="superlative-modifiers">
      <h4>Superlative Modifiers (Emphasize "The Best/Biggest")</h4>
      
      <div class="easily">
        <h5>EASILY (sin duda, fácilmente):</h5>
        <ul>
          <li>Mount Everest is <strong>easily the tallest</strong> mountain</li>
          <li>Paris is <strong>easily the most visited</strong> city in Europe</li>
        </ul>
        <p>Meaning: Without any doubt, clearly</p>
      </div>
      
      <div class="by-far">
        <h5>BY FAR (por mucho, de lejos) - STRONGEST emphasis:</h5>
        <ul>
          <li>The Nile is <strong>by far the longest</strong> river in Africa</li>
          <li>This is <strong>by far the best</strong> restaurant I've tried</li>
        </ul>
        <p>Meaning: With a huge margin, incomparably</p>
      </div>
    </div>

    <div class="one-of-structure-critical">
      <h4>"One Of" Structure (CRITICAL!) ⚠️</h4>
      <p><strong>Structure: ONE OF + THE + SUPERLATIVE + PLURAL NOUN</strong></p>
      
      <ul>
        <li>✅ Paris is one of the most beautiful <strong>cities</strong> (PLURAL!)</li>
        <li>✅ The Eiffel Tower is one of the tallest <strong>structures</strong></li>
        <li>✅ This is one of the best <strong>restaurants</strong></li>
      </ul>
      
      <p class="error-warning">❌ COMMON ERROR:</p>
      <ul>
        <li>❌ "One of the most beautiful city" (WRONG!)</li>
        <li>✅ "One of the most beautiful cities" (CORRECT!)</li>
      </ul>
      
      <p><strong>Why plural?</strong> "One of" = one example from a GROUP. The group = plural!</p>
    </div>
  `,

  iconType: "globe",

  sets: [
    // ========================================================================
    // SET 1: BASIC COMPARATIVES REVIEW 📊
    // ========================================================================
    {
      id: "m7-u8-s1",
      title: "Set 1: Comparative Review 📊",
      context: `**Comparing Two Cities**

Friend: "I'm choosing between visiting Barcelona or Madrid. Can you compare them?"

You: "Sure! Let me tell you the differences..."`,
      
      questions: [
        {
          type: "match",
          q: "Match adjectives with their comparative forms:",
          pairs: [
            { left: "Big", right: "Bigger" },
            { left: "Hot", right: "Hotter" },
            { left: "Beautiful", right: "More beautiful" },
            { left: "Expensive", right: "More expensive" }
          ],
          successtip: "✓ COMPARATIVE FORMS!\nShort adjectives + -er. Long adjectives = more + adjective! Foundation for modifiers! 📊"
        },
        {
          type: "mc",
          q: "You: 'Barcelona is ___ than Madrid.' (warm)",
          options: [
            "warmer",
            "more warm",
            "warmest"
          ],
          a: "warmer",
          successtip: "✓ SHORT ADJECTIVE!\n'Warm' = one syllable = add -er. Barcelona's coastal location = warmer! 🌡️☀️"
        },
        {
          type: "blank",
          q: "You: 'Madrid is ___ than Barcelona.' (large)",
          a: "larger",
          accept: { ignoreCase: true },
          successtip: "✓ CAPITAL SIZE!\n'Larger' = comparative. Madrid = Spain's capital, bigger population! 🏙️"
        },
        {
          type: "mc",
          q: "You: 'Barcelona is ___ than Madrid in my opinion.' (beautiful)",
          options: [
            "more beautiful",
            "beautifuler",
            "most beautiful"
          ],
          a: "more beautiful",
          successtip: "✓ LONG ADJECTIVE!\n'Beautiful' = 3 syllables = 'more beautiful'. Long adjectives don't use -er! 🎨"
        },
        {
          type: "order",
          q: "Traduce: París es más cara que Berlín",
          a: "Paris is more expensive than Berlin",
          successtip: "✓ COST COMPARISON!\n'More expensive' = long adjective. Paris = notoriously expensive! 💰🇫🇷"
        },
        {
          type: "mc",
          q: "Friend: 'Which is older?' You: 'Madrid is ___ than Barcelona.' (old)",
          options: [
            "older",
            "more old",
            "oldest"
          ],
          a: "older",
          successtip: "✓ HISTORICAL AGE!\n'Older' = comparative. Both ancient, but Madrid founded earlier! 🏛️"
        },
        {
          type: "blank",
          q: "You: 'Barcelona is ___ than Madrid.' (crowded with tourists)",
          a: "more crowded",
          accept: { ignoreCase: true },
          successtip: "✓ TOURIST VOLUME!\n'More crowded' = Barcelona's beaches = huge tourist draw! Las Ramblas packed! 👥🏖️"
        },
        {
          type: "mc",
          q: "You: 'The food in both cities is amazing, but Barcelona is ___ for seafood.' (good)",
          options: [
            "better",
            "more good",
            "gooder"
          ],
          a: "better",
          successtip: "✓ IRREGULAR!\n'Good' → 'better' (irregular comparative). Coastal Barcelona = amazing seafood! 🦐🐟"
        },
        {
          type: "order",
          q: "Traduce: Londres es más grande que Edimburgo",
          a: "London is bigger than Edinburgh",
          successtip: "✓ UK CITIES!\n'Bigger' = short adjective. London = massive capital, Edinburgh = smaller but beautiful! 🇬🇧"
        },
        {
          type: "mc",
          q: "Summary: For long adjectives (3+ syllables), we use:",
          options: [
            "More + adjective (more beautiful)",
            "Adjective + -er (beautifuler)",
            "Most + adjective"
          ],
          a: "More + adjective (more beautiful)",
          successtip: "✓ LONG ADJECTIVE RULE!\n'More' + long adjectives. Foundation for adding modifiers next! 📚"
        }
      ]
    },

    // ========================================================================
    // SET 2: BASIC SUPERLATIVES REVIEW 🏆
    // ========================================================================
    {
      id: "m7-u8-s2",
      title: "Set 2: Superlative Review 🏆",
      context: `**World Records & Rankings**

Quiz Master: "Time to test your knowledge of world records!"`,
      
      questions: [
        {
          type: "match",
          q: "Match adjectives with their superlative forms:",
          pairs: [
            { left: "Tall", right: "Tallest" },
            { left: "Long", right: "Longest" },
            { left: "Popular", right: "Most popular" },
            { left: "Beautiful", right: "Most beautiful" }
          ],
          successtip: "✓ SUPERLATIVE FORMS!\nShort = the -est. Long = the most + adjective! World records time! 🏆"
        },
        {
          type: "mc",
          q: "Quiz: 'Mount Everest is ___ mountain in the world.'",
          options: [
            "the tallest",
            "the most tall",
            "the taller"
          ],
          a: "the tallest",
          successtip: "✓ WORLD'S HIGHEST!\n'The tallest' = superlative. Everest = 8,849m! Nepal/Tibet border! 🏔️🌍"
        },
        {
          type: "blank",
          q: "Quiz: 'The Nile is ___ ___ river in Africa.' (long)",
          a: "the longest",
          accept: { ignoreCase: true },
          successtip: "✓ AFRICAN GIANT!\n'The longest' = superlative. Nile ≈ 6,650 km! Flows through 11 countries! 🌊🇪🇬"
        },
        {
          type: "mc",
          q: "Quiz: 'Paris is ___ city in Europe.' (visited)",
          options: [
            "the most visited",
            "the visitedest",
            "the more visited"
          ],
          a: "the most visited",
          successtip: "✓ TOURISM CAPITAL!\n'The most visited' = long adjective. Paris = ~19 million tourists/year! 🇫🇷🗼"
        },
        {
          type: "order",
          q: "Traduce: Tokio es una de las ciudades más caras del mundo",
          a: "Tokyo is one of the most expensive cities in the world",
          successtip: "✓ COST LEADER!\n'One of the most expensive' = superlative + 'one of' structure (coming next!)! 💴🇯🇵"
        },
        {
          type: "mc",
          q: "Quiz: 'The Sahara is ___ desert on Earth.' (large)",
          options: [
            "the largest",
            "the most large",
            "the larger"
          ],
          a: "the largest",
          successtip: "✓ DESERT GIANT!\n'The largest' = short adjective. Sahara ≈ 9 million km²! Size of USA! 🏜️🐪"
        },
        {
          type: "blank",
          q: "Quiz: 'Antarctica is ___ ___ continent.' (cold)",
          a: "the coldest",
          accept: { ignoreCase: true },
          successtip: "✓ FROZEN EXTREME!\n'The coldest' = double consonant! Antarctica = -89.2°C recorded! 🥶🐧"
        },
        {
          type: "mc",
          q: "Quiz: 'The Grand Canyon is one of ___ natural wonders.' (impressive)",
          options: [
            "the most impressive",
            "the impressivest",
            "the more impressive"
          ],
          a: "the most impressive",
          successtip: "✓ NATURAL MARVEL!\n'The most impressive' = long adjective. Grand Canyon = 446 km long! 🇺🇸🏞️"
        },
        {
          type: "order",
          q: "Traduce: El Burj Khalifa es el edificio más alto del mundo",
          a: "The Burj Khalifa is the tallest building in the world",
          successtip: "✓ DUBAI GIANT!\n'The tallest' = superlative. Burj Khalifa = 828m, 163 floors! 🇦🇪🏢☁️"
        },
        {
          type: "mc",
          q: "Summary: Superlatives compare:",
          options: [
            "Three or more things (the best of all)",
            "Only two things",
            "Cannot be used for comparisons"
          ],
          a: "Three or more things (the best of all)",
          successtip: "✓ GROUP COMPARISON!\nSuperlatives = pick THE winner from a group! Foundation for modifiers! 🏆✨"
        }
      ]
    },

    // ========================================================================
    // SET 3: BIG DIFFERENCES - MUCH & A LOT 📏
    // ========================================================================
    {
      id: "m7-u8-s3",
      title: "Set 3: Big Differences - Much & A Lot 📏",
      context: `**Comparing Dramatically Different Places**

Travel Agent: "These destinations are VERY different from each other..."`,
      
      questions: [
        {
          type: "mc",
          q: "Agent: 'Dubai is ___ hotter than London in summer!'",
          options: [
            "much",
            "a little",
            "slightly"
          ],
          a: "much",
          successtip: "✓ HUGE TEMPERATURE GAP!\n'Much hotter' = BIG difference! Dubai 40°C+ vs London 20°C! Use 'much' for dramatic differences! 🌡️🔥"
        },
        {
          type: "blank",
          q: "Agent: 'Tokyo is ___ ___ expensive than Bangkok.' (a lot/much + more expensive)",
          a: "much more",
          accept: { ignoreCase: true, alts: ["a lot more", "much / more", "a lot / more"] },
          successtip: "✓ COST DIFFERENCE!\n'Much more' or 'a lot more' = both work! Tokyo $$$ vs Bangkok $! Big difference! 💰"
        },
        {
          type: "mc",
          q: "Agent: 'New York is ___ bigger than Boston.'",
          options: [
            "much / a lot",
            "slightly",
            "a bit"
          ],
          a: "much / a lot",
          successtip: "✓ SIZE DIFFERENCE!\n'Much/a lot bigger' = NYC 8M people, Boston 700K! Huge difference! 🏙️"
        },
        {
          type: "order",
          q: "Traduce: París es mucho más cara que Praga",
          a: "Paris is much more expensive than Prague",
          successtip: "✓ PRICE GAP!\n'Much more expensive' = significant cost difference! Paris vs Prague budget = huge! 💶"
        },
        {
          type: "mc",
          q: "When do we use 'much' or 'a lot' with comparatives?",
          options: [
            "When the difference is BIG and significant",
            "When the difference is tiny",
            "They mean exactly opposite things"
          ],
          a: "When the difference is BIG and significant",
          successtip: "✓ BIG DIFFERENCE RULE!\n'Much/a lot' = emphasize LARGE difference. Makes comparison stronger! 📏💪"
        },
        {
          type: "blank",
          q: "Tourist: 'Is Rome bigger than Florence?' Agent: 'Yes, ___ bigger!' (emphasize large difference)",
          a: "much",
          accept: { ignoreCase: true, alts: ["a lot"] },
          successtip: "✓ DRAMATIC SIZE!\nRome 2.8M, Florence 380K! 'Much bigger' = perfect! Emphasizes huge gap! 🏛️"
        },
        {
          type: "mc",
          q: "Agent: 'The Burj Khalifa is ___ taller than most buildings - 828 meters!'",
          options: [
            "much / a lot",
            "slightly",
            "a bit"
          ],
          a: "much / a lot",
          successtip: "✓ EXTREME HEIGHT!\n'Much/a lot taller' = Burj 828m vs most buildings <200m! Massive difference! 🏢☁️"
        },
        {
          type: "order",
          q: "Traduce: China es mucho más grande que Japón",
          a: "China is much bigger than Japan",
          successtip: "✓ LAND AREA!\n'Much bigger' = China 9.6M km², Japan 378K km²! Dramatic size difference! 🌍"
        },
        {
          type: "mc",
          q: "'Much' and 'a lot' in comparisons are:",
          options: [
            "Interchangeable - both show big differences",
            "Completely different meanings",
            "Only 'much' can be used"
          ],
          a: "Interchangeable - both show big differences",
          successtip: "✓ SYNONYMOUS!\n'Much' = 'a lot' for comparisons! Both emphasize BIG difference! Choose either! 🔄"
        },
        {
          type: "mc",
          q: "Complete naturally: 'Mount Everest is ___ taller than any other mountain.'",
          options: [
            "much",
            "slightly",
            "a tiny bit"
          ],
          a: "much",
          successtip: "✓ SUPREME HEIGHT!\n'Much taller' = Everest 8,849m, K2 8,611m. Still significant! Record holder! 🏔️👑"
        }
      ]
    },

    // ========================================================================
    // SET 4: SMALL DIFFERENCES - SLIGHTLY & A BIT 📐
    // ========================================================================
    {
      id: "m7-u8-s4",
      title: "Set 4: Small Differences - Slightly & A Bit 📐",
      context: `**Comparing Similar Places**

Real Estate Agent: "These two apartments are very similar, with minor differences..."`,
      
      questions: [
        {
          type: "mc",
          q: "Agent: 'This apartment is ___ larger than the other one - just 5 square meters more.'",
          options: [
            "slightly / a bit",
            "much",
            "a lot"
          ],
          a: "slightly / a bit",
          successtip: "✓ MINOR DIFFERENCE!\n'Slightly/a bit' = SMALL difference! 5m² = noticeable but minor! 📐"
        },
        {
          type: "blank",
          q: "Agent: 'This building is ___ older - built in 1985, the other in 1990.' (small difference)",
          a: "slightly",
          accept: { ignoreCase: true, alts: ["a bit", "a little"] },
          successtip: "✓ SMALL AGE GAP!\n'Slightly/a bit/a little older' = 5 years difference = minor! All three work! 🏢📅"
        },
        {
          type: "mc",
          q: "When do we use 'slightly', 'a bit', or 'a little'?",
          options: [
            "When the difference is small but noticeable",
            "When the difference is huge",
            "When things are exactly the same"
          ],
          a: "When the difference is small but noticeable",
          successtip: "✓ SMALL DIFFERENCE RULE!\n'Slightly/a bit/a little' = subtle differences. Makes comparison precise! 📏✨"
        },
        {
          type: "order",
          q: "Traduce: Londres es ligeramente más grande que Berlín",
          a: "London is slightly bigger than Berlin",
          successtip: "✓ COMPARABLE CITIES!\n'Slightly bigger' = London 9M, Berlin 3.7M. Noticeable but both major capitals! 🏙️"
        },
        {
          type: "mc",
          q: "Tourist: 'Is Barcelona warmer than Madrid?' Guide: 'Yes, but only ___ warmer.'",
          options: [
            "slightly / a bit",
            "much",
            "a lot"
          ],
          a: "slightly / a bit",
          successtip: "✓ MILD DIFFERENCE!\n'Slightly warmer' = Barcelona coastal, Madrid inland. Both Mediterranean climate! 🌡️"
        },
        {
          type: "blank",
          q: "Comparing cities: 'Rome is ___ more touristy than Milan.' (small difference)",
          a: "a bit",
          accept: { ignoreCase: true, alts: ["slightly", "a little"] },
          successtip: "✓ TOURIST VOLUME!\n'A bit more touristy' = Rome famous ruins, but Milan also popular! Both get tourists! 👥"
        },
        {
          type: "mc",
          q: "'Slightly', 'a bit', and 'a little' are:",
          options: [
            "Interchangeable - all show small differences",
            "Completely different",
            "Only 'slightly' can be used"
          ],
          a: "Interchangeable - all show small differences",
          successtip: "✓ SYNONYMOUS GROUP!\nAll three = small differences! 'Slightly' = more formal. 'A bit' = casual! 🔄"
        },
        {
          type: "order",
          q: "Traduce: Este restaurante es un poco más caro que ese",
          a: "This restaurant is a bit more expensive than that one",
          successtip: "✓ PRICE DIFFERENCE!\n'A bit more expensive' = minor price difference. Both similar range! 💰"
        },
        {
          type: "mc",
          q: "Complete: 'Sydney is ___ warmer than Melbourne - not a huge difference.'",
          options: [
            "slightly / a bit",
            "much",
            "a lot"
          ],
          a: "slightly / a bit",
          successtip: "✓ AUSTRALIAN CLIMATE!\n'Slightly warmer' = Sydney 23°C average, Melbourne 20°C. Small gap! 🇦🇺☀️"
        },
        {
          type: "mc",
          q: "The difference between 'much' and 'slightly' is:",
          options: [
            "'Much' = big difference, 'slightly' = small difference",
            "They mean the same thing",
            "'Much' = small, 'slightly' = big"
          ],
          a: "'Much' = big difference, 'slightly' = small difference",
          successtip: "✓ MODIFIER SCALE!\nMuch/a lot = BIG. Slightly/a bit = SMALL. Choose based on actual difference! 📊"
        }
      ]
    },

    // ========================================================================
    // SET 5: EMPHASIS - EASILY ✨
    // ========================================================================
    {
      id: "m7-u8-s5",
      title: "Set 5: Superlative Emphasis - Easily ✨",
      context: `**Clear Winners**

Documentary: "Some records are absolutely undeniable..."`,
      
      questions: [
        {
          type: "mc",
          q: "Narrator: 'Mount Everest is ___ the tallest mountain in the world.'",
          options: [
            "easily",
            "hardly",
            "barely"
          ],
          a: "easily",
          successtip: "✓ NO DOUBT!\n'Easily the tallest' = without question! Everest 8,849m, next is K2 8,611m. Clear winner! 🏔️👑"
        },
        {
          type: "blank",
          q: "Narrator: 'The Sahara is ___ the largest hot desert.' (emphasis = without doubt)",
          a: "easily",
          accept: { ignoreCase: true },
          successtip: "✓ CLEAR LEADER!\n'Easily the largest' = no competition! Sahara = 9M km²! Obvious winner! 🏜️✨"
        },
        {
          type: "mc",
          q: "What does 'easily' mean before a superlative?",
          options: [
            "Without any doubt, clearly",
            "With difficulty",
            "Maybe, possibly"
          ],
          a: "Without any doubt, clearly",
          successtip: "✓ EMPHASIS WORD!\n'Easily' = sin duda, fácilmente. Emphasizes the superlative = no question! 💯"
        },
        {
          type: "order",
          q: "Traduce: París es fácilmente la ciudad más visitada de Europa",
          a: "Paris is easily the most visited city in Europe",
          successtip: "✓ TOURISM LEADER!\n'Easily the most visited' = Paris ~19M/year, London ~12M. Clear winner! 🇫🇷🗼"
        },
        {
          type: "mc",
          q: "Travel blogger: 'Tokyo is ___ the most organized city I've visited.'",
          options: [
            "easily",
            "hardly",
            "barely"
          ],
          a: "easily",
          successtip: "✓ OPINION EMPHASIS!\n'Easily the most organized' = strong personal opinion! No other city compares! 🇯🇵✨"
        },
        {
          type: "blank",
          q: "Food critic: 'This is ___ the best restaurant in the city.' (no doubt)",
          a: "easily",
          accept: { ignoreCase: true },
          successtip: "✓ CLEAR FAVORITE!\n'Easily the best' = emphatic opinion! Much better than competitors! 🍽️⭐"
        },
        {
          type: "mc",
          q: "'Easily' with superlatives makes them:",
          options: [
            "Stronger and more emphatic",
            "Weaker and less certain",
            "Neutral, no change"
          ],
          a: "Stronger and more emphatic",
          successtip: "✓ AMPLIFIER!\n'Easily' = strengthens superlative! Makes statement more confident! 💪✨"
        },
        {
          type: "order",
          q: "Traduce: El Grand Canyon es fácilmente una de las maravillas naturales más impresionantes",
          a: "The Grand Canyon is easily one of the most impressive natural wonders",
          successtip: "✓ NATURAL MARVEL!\n'Easily one of...' = combining emphasis + 'one of' structure! Advanced! 🏞️"
        },
        {
          type: "mc",
          q: "Tech review: 'This is ___ the fastest phone on the market right now.'",
          options: [
            "easily",
            "hardly",
            "barely"
          ],
          a: "easily",
          successtip: "✓ TECH LEADER!\n'Easily the fastest' = no competition in speed! Clear performance winner! 📱⚡"
        },
        {
          type: "mc",
          q: "Position of 'easily' with superlatives:",
          options: [
            "Before 'the' (easily the tallest)",
            "After the adjective",
            "Can go anywhere"
          ],
          a: "Before 'the' (easily the tallest)",
          successtip: "✓ WORD ORDER!\n'Easily + THE + superlative' = fixed position! 'Easily the best' ✅ 📍"
        }
      ]
    },

    // ========================================================================
    // SET 6: STRONG EMPHASIS - BY FAR 💪
    // ========================================================================
    {
      id: "m7-u8-s6",
      title: "Set 6: Strongest Emphasis - By Far 💪",
      context: `**No Competition**

Awards Ceremony: "And the winners with NO competition whatsoever..."`,
      
      questions: [
        {
          type: "mc",
          q: "Host: 'This year's winner is ___ the best film we've seen!'",
          options: [
            "by far",
            "hardly",
            "barely"
          ],
          a: "by far",
          successtip: "✓ STRONGEST EMPHASIS!\n'By far the best' = no contest! Much better than all others! Supreme! 🏆✨"
        },
        {
          type: "blank",
          q: "Critic: 'She is ___ ___ the most talented actor in the cast.' (strongest emphasis)",
          a: "by far",
          accept: { ignoreCase: true, alts: ["by / far"] },
          successtip: "✓ INCOMPARABLE!\n'By far' = por mucho, de lejos. Strongest possible emphasis! No comparison! 🎭⭐"
        },
        {
          type: "mc",
          q: "'By far' is ___ than 'easily':",
          options: [
            "Stronger - shows even bigger margin",
            "Weaker",
            "Exactly the same"
          ],
          a: "Stronger - shows even bigger margin",
          successtip: "✓ EMPHASIS SCALE!\n'Easily' = strong. 'By far' = STRONGEST! Huge margin of difference! 💪📊"
        },
        {
          type: "order",
          q: "Traduce: Este es por mucho el mejor restaurante de la ciudad",
          a: "This is by far the best restaurant in the city",
          successtip: "✓ UNMATCHED QUALITY!\n'By far the best' = no other restaurant even close! Supreme winner! 🍽️👑"
        },
        {
          type: "mc",
          q: "Geographer: 'The Nile is ___ the longest river in Africa.'",
          options: [
            "by far",
            "slightly",
            "a bit"
          ],
          a: "by far",
          successtip: "✓ DOMINANT LENGTH!\n'By far the longest' = Nile 6,650km, next Congo 4,700km. Huge gap! 🌊🇪🇬"
        },
        {
          type: "blank",
          q: "Traveler: 'Iceland was ___ ___ the most beautiful country I visited.' (strongest)",
          a: "by far",
          accept: { ignoreCase: true, alts: ["by / far"] },
          successtip: "✓ PERSONAL FAVORITE!\n'By far the most beautiful' = emphatic opinion! No other country even close! 🇮🇸✨"
        },
        {
          type: "mc",
          q: "Teacher: 'She's ___ the smartest student in the class - nobody else comes close.'",
          options: [
            "by far",
            "hardly",
            "barely"
          ],
          a: "by far",
          successtip: "✓ ACADEMIC LEADER!\n'By far the smartest' = huge gap between her and others! Clear standout! 📚🌟"
        },
        {
          type: "order",
          q: "Traduce: Nueva York es por lejos la ciudad más diversa que he visitado",
          a: "New York is by far the most diverse city I have visited",
          successtip: "✓ CULTURAL CAPITAL!\n'By far the most diverse' = NYC incomparable diversity! 200+ languages! 🗽🌍"
        },
        {
          type: "mc",
          q: "Position of 'by far':",
          options: [
            "Before 'the' (by far the best)",
            "After the adjective",
            "At the end of sentence"
          ],
          a: "Before 'the' (by far the best)",
          successtip: "✓ FIXED POSITION!\n'By far + THE + superlative' = correct order! 'By far the tallest' ✅ 📍"
        },
        {
          type: "mc",
          q: "Summary: 'Easily' vs 'By far':",
          options: [
            "Both add emphasis, but 'by far' is strongest",
            "They mean opposite things",
            "'Easily' is stronger"
          ],
          a: "Both add emphasis, but 'by far' is strongest",
          successtip: "✓ EMPHASIS HIERARCHY!\nGood < Easily the best < By far the best! 'By far' = maximum emphasis! 📊💯"
        }
      ]
    },

    // ========================================================================
    // SET 7: "ONE OF" STRUCTURE 🌟 - CRITICAL PLURAL!
    // ========================================================================
    {
      id: "m7-u8-s7",
      title: "Set 7: 'One Of' Structure - MUST Be Plural! 🌟",
      context: `**Travel Highlights**

Blogger: "Let me share my favorite places..."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is CORRECT?",
          options: [
            "Paris is one of the most beautiful cities",
            "Paris is one of the most beautiful city",
            "Both are correct"
          ],
          a: "Paris is one of the most beautiful cities",
          successtip: "✓ PLURAL REQUIRED!\n'One of the... cities' (PLURAL!) = critical rule! One example from a group! 🏙️✅"
        },
        {
          type: "blank",
          q: "Blogger: 'The Eiffel Tower is one of the tallest ___ in Paris.' (structure/building)",
          a: "structures",
          accept: { ignoreCase: true, alts: ["buildings"] },
          successtip: "✓ MUST BE PLURAL!\n'One of the tallest structures' = PLURAL! One from a group of tall structures! 🗼"
        },
        {
          type: "mc",
          q: "Why must the noun be plural after 'one of the...'?",
          options: [
            "Because 'one of' means one example from a GROUP (plural)",
            "Just a grammar rule with no reason",
            "It can be singular or plural"
          ],
          a: "Because 'one of' means one example from a GROUP (plural)",
          successtip: "✓ LOGICAL REASON!\n'One of' = picking ONE from MANY. The 'many' = plural! Makes sense! 🎯"
        },
        {
          type: "order",
          q: "Traduce: Tokio es una de las ciudades más caras del mundo",
          a: "Tokyo is one of the most expensive cities in the world",
          successtip: "✓ COST EXAMPLE!\n'One of the most expensive cities' = plural! Tokyo = one example from many expensive cities! 💴"
        },
        {
          type: "mc",
          q: "Blogger: 'This was one of the best ___ I've ever tried!' (restaurant)",
          options: [
            "restaurants",
            "restaurant",
            "Both work"
          ],
          a: "restaurants",
          successtip: "✓ FOOD HIGHLIGHT!\n'One of the best restaurants' = PLURAL! One from your list of good restaurants! 🍽️"
        },
        {
          type: "blank",
          q: "Fix error: 'Mount Everest is one of the tallest mountain' → '...one of the tallest ___'",
          a: "mountains",
          accept: { ignoreCase: true },
          successtip: "✓ ERROR CORRECTED!\n'Mountains' (PLURAL!) = correct! One mountain from the group of tall mountains! 🏔️✅"
        },
        {
          type: "mc",
          q: "Blogger: 'Barcelona is one of the most vibrant ___ in Spain.' (city)",
          options: [
            "cities",
            "city",
            "citys"
          ],
          a: "cities",
          successtip: "✓ SPANISH HIGHLIGHT!\n'One of the most vibrant cities' = plural! Barcelona = one of Spain's energetic cities! 🇪🇸✨"
        },
        {
          type: "order",
          q: "Fix: 'The Louvre is one of the best museum' → 'The Louvre is one of the best ___'",
          a: "museums",
          successtip: "✓ CULTURAL ICON!\n'Museums' (plural) = correct! Louvre = one great museum from many! 🎨🏛️"
        },
        {
          type: "mc",
          q: "Complete: 'She's one of the smartest ___ in our class.' (student)",
          options: [
            "students",
            "student",
            "Both work"
          ],
          a: "students",
          successtip: "✓ ACADEMIC EXAMPLE!\n'One of the smartest students' = plural! One smart student from the group! 📚👥"
        },
        {
          type: "mc",
          q: "CRITICAL RULE: After 'one of the' + superlative, the noun is:",
          options: [
            "ALWAYS PLURAL (cities, mountains, restaurants)",
            "Always singular",
            "Can be either"
          ],
          a: "ALWAYS PLURAL (cities, mountains, restaurants)",
          successtip: "✓ GOLDEN RULE!\n'One of' + superlative + PLURAL = fixed structure! Most common error at all levels! ⚠️✅"
        }
      ]
    },

    // ========================================================================
    // SET 8: "ONE OF" - INTENSIVE PRACTICE 💯
    // ========================================================================
    {
      id: "m7-u8-s8",
      title: "Set 8: 'One Of' - Intensive Practice 💯",
      context: `**World Travel Experiences**

Travel vlogger: "After visiting 50 countries, here are my highlights..."`,
      
      questions: [
        {
          type: "mc",
          q: "Vlogger: 'Iceland is one of the most stunning ___ I've seen.'",
          options: [
            "countries (plural!)",
            "country (singular)",
            "Both work"
          ],
          a: "countries (plural!)",
          successtip: "✓ NORDIC BEAUTY!\n'One of the most stunning countries' = Iceland among beautiful nations! 🇮🇸✨"
        },
        {
          type: "blank",
          q: "Vlogger: 'The Northern Lights are one of the most magical ___ on Earth.' (phenomenon → plural?)",
          a: "phenomena",
          accept: { ignoreCase: true, alts: ["things", "sights", "experiences"] },
          successtip: "✓ IRREGULAR PLURAL!\n'Phenomena' = plural of 'phenomenon'! Or 'things/sights/experiences' work! Aurora = magical! 🌌✨"
        },
        {
          type: "mc",
          q: "Vlogger: 'Santorini is one of the most romantic ___ for a honeymoon.'",
          options: [
            "destinations",
            "destination",
            "Both work"
          ],
          a: "destinations",
          successtip: "✓ HONEYMOON HAVEN!\n'One of the most romantic destinations' = plural! Santorini = Greek island paradise! 🇬🇷💙"
        },
        {
          type: "order",
          q: "Traduce: La Gran Muralla es una de las estructuras más largas del mundo",
          a: "The Great Wall is one of the longest structures in the world",
          successtip: "✓ ANCIENT MARVEL!\n'One of the longest structures' = plural! Great Wall ≈ 21,000 km! 🇨🇳🏯"
        },
        {
          type: "mc",
          q: "Vlogger: 'This was one of the worst ___ I've ever experienced!' (flight)",
          options: [
            "flights",
            "flight",
            "Both work"
          ],
          a: "flights",
          successtip: "✓ TRAVEL NIGHTMARE!\n'One of the worst flights' = plural! Even negative superlatives need plural! ✈️😰"
        },
        {
          type: "blank",
          q: "Vlogger: 'Petra is one of the most impressive archaeological ___ I've visited.' (site)",
          a: "sites",
          accept: { ignoreCase: true },
          successtip: "✓ JORDANIAN WONDER!\n'One of the most impressive sites' = plural! Petra = ancient carved city! 🇯🇴🏛️"
        },
        {
          type: "mc",
          q: "Vlogger: 'This hotel has one of the best ___ I've ever stayed in!' (room)",
          options: [
            "rooms",
            "room",
            "Both work"
          ],
          a: "rooms",
          successtip: "✓ LUXURY STAY!\n'One of the best rooms' = plural! One great room among many hotel rooms! 🏨✨"
        },
        {
          type: "order",
          q: "Fix error: 'Tokyo has one of the busiest metro system' → 'one of the busiest ___'",
          a: "metro systems",
          successtip: "✓ TRANSPORT MARVEL!\n'Metro systems' (plural!) = correct! Tokyo metro = one of world's busiest! 🚇🇯🇵"
        },
        {
          type: "mc",
          q: "Vlogger: 'Machu Picchu is one of the greatest ___ of ancient civilization.'",
          options: [
            "achievements",
            "achievement",
            "Both work"
          ],
          a: "achievements",
          successtip: "✓ INCA MASTERPIECE!\n'One of the greatest achievements' = plural! Machu Picchu = architectural wonder! 🇵🇪🏔️"
        },
        {
          type: "mc",
          q: "Summary check: 'One of the best ___' - the blank MUST be:",
          options: [
            "PLURAL noun (cities, places, restaurants)",
            "Singular noun",
            "Can be either"
          ],
          a: "PLURAL noun (cities, places, restaurants)",
          successtip: "✓ RULE MASTERED!\n'One of' + superlative + PLURAL = no exceptions! Even advanced learners make this error! 💯✅"
        }
      ]
    },

    // ========================================================================
    // SET 9: GEOGRAPHIC COMPARISONS 🌍
    // ========================================================================
    {
      id: "m7-u8-s9",
      title: "Set 9: Geographic Comparisons 🌍",
      context: `**Geography Quiz Show**

Quiz Host: "Test your knowledge of world geography with comparisons!"`,
      
      questions: [
        {
          type: "mc",
          q: "Host: 'The Amazon is ___ longer than the Thames.'",
          options: [
            "much (big difference!)",
            "slightly",
            "a bit"
          ],
          a: "much (big difference!)",
          successtip: "✓ DRAMATIC LENGTH!\n'Much longer' = Amazon 6,400km vs Thames 346km! Massive difference! 🌊"
        },
        {
          type: "blank",
          q: "Host: 'Russia is ___ ___ the largest country by area.' (strongest emphasis)",
          a: "by far",
          accept: { ignoreCase: true, alts: ["by / far", "easily"] },
          successtip: "✓ LAND GIANT!\n'By far the largest' = Russia 17M km², Canada 10M km². Huge margin! 🇷🇺🌍"
        },
        {
          type: "mc",
          q: "Host: 'The Mariana Trench is ___ the deepest place on Earth - no competition!'",
          options: [
            "easily / by far",
            "slightly",
            "a bit"
          ],
          a: "easily / by far",
          successtip: "✓ OCEAN DEPTH!\n'Easily/by far the deepest' = 10,994m deep! No other trench close! 🌊🏔️⬇️"
        },
        {
          type: "order",
          q: "Traduce: El Sahara es por mucho el desierto cálido más grande",
          a: "The Sahara is by far the largest hot desert",
          successtip: "✓ DESERT KING!\n'By far the largest' = Sahara 9M km², next is Arabian 2.3M km²! Dominant! 🏜️🐪"
        },
        {
          type: "mc",
          q: "Host: 'Mount Everest is one of the most dangerous ___ to climb.' (mountain)",
          options: [
            "mountains (plural!)",
            "mountain",
            "Both work"
          ],
          a: "mountains (plural!)",
          successtip: "✓ DEADLY PEAKS!\n'One of the most dangerous mountains' = plural! Everest + K2 + others = deadly! 🏔️⚠️"
        },
        {
          type: "blank",
          q: "Host: 'Canada is ___ larger than Mexico.' (noticeable but not huge difference)",
          a: "a bit",
          accept: { ignoreCase: true, alts: ["slightly", "a little"] },
          successtip: "✓ SIZE COMPARISON!\nCanada 10M km², Mexico 2M km². Actually 'much' also works! But 'a bit' fits quiz! 🇨🇦🇲🇽"
        },
        {
          type: "mc",
          q: "Host: 'Vatican City is ___ the smallest country in the world.'",
          options: [
            "easily / by far",
            "slightly",
            "a bit"
          ],
          a: "easily / by far",
          successtip: "✓ MICRO STATE!\n'Easily the smallest' = Vatican 0.44 km²! Monaco 2 km². Clear winner! 🇻🇦⛪"
        },
        {
          type: "order",
          q: "Traduce: El Nilo es uno de los ríos más largos del mundo",
          a: "The Nile is one of the longest rivers in the world",
          successtip: "✓ ANCIENT RIVER!\n'One of the longest rivers' = plural! Nile vs Amazon debate! Both ~6,500km! 🌊🇪🇬"
        },
        {
          type: "mc",
          q: "Host: 'Antarctica is ___ colder than any other continent.'",
          options: [
            "much",
            "slightly",
            "a tiny bit"
          ],
          a: "much",
          successtip: "✓ FROZEN EXTREME!\n'Much colder' = Antarctica -50°C average, next coldest much warmer! Extreme! 🐧❄️"
        },
        {
          type: "mc",
          q: "Host: 'The Pacific Ocean is ___ the largest ocean - it's enormous!'",
          options: [
            "by far / easily",
            "slightly",
            "a bit"
          ],
          a: "by far / easily",
          successtip: "✓ OCEAN GIANT!\n'By far the largest' = Pacific 165M km², Atlantic 82M km². Massive! 🌊🌍"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE MASTERY ✈️
    // ========================================================================
    {
      id: "m7-u8-s10",
      title: "Set 10: Travel Blog - Complete Mastery ✈️",
      context: `**FINAL CHALLENGE**

You're writing a travel blog comparing all the places you've visited. Use ALL modifiers and structures naturally!`,
      
      questions: [
        {
          type: "mc",
          q: "Blog: 'I've visited 30 countries, and Iceland is ___ the most beautiful.'",
          options: [
            "easily / by far",
            "slightly",
            "a bit"
          ],
          a: "easily / by far",
          successtip: "✓ EMPHATIC OPINION!\n'Easily/by far the most beautiful' = strong personal ranking! Iceland = stunning! 🇮🇸✨"
        },
        {
          type: "blank",
          q: "Blog: 'Paris was ___ more expensive than Prague.' (big price difference)",
          a: "much",
          accept: { ignoreCase: true, alts: ["a lot", "much / more", "a lot / more"] },
          successtip: "✓ COST COMPARISON!\n'Much more expensive' = significant budget difference! Paris $$$ vs Prague $! 💰"
        },
        {
          type: "mc",
          q: "Blog: 'Barcelona was ___ warmer than Madrid - not a huge difference though.' (small)",
          options: [
            "slightly / a bit",
            "much",
            "a lot"
          ],
          a: "slightly / a bit",
          successtip: "✓ MINOR CLIMATE!\n'Slightly warmer' = small temperature difference! Both Mediterranean! 🌡️☀️"
        },
        {
          type: "order",
          q: "Traduce: Tokio es una de las ciudades más organizadas que he visitado",
          a: "Tokyo is one of the most organized cities I have visited",
          successtip: "✓ JAPANESE EFFICIENCY!\n'One of the most organized cities' = plural! Tokyo = famously efficient! 🇯🇵✨"
        },
        {
          type: "mc",
          q: "Blog: 'The food in Italy was ___ better than I expected!'",
          options: [
            "much / a lot",
            "slightly",
            "a tiny bit"
          ],
          a: "much / a lot",
          successtip: "✓ CULINARY SURPRISE!\n'Much better' = exceeded expectations significantly! Italian food = amazing! 🇮🇹🍝"
        },
        {
          type: "blank",
          q: "Blog: 'Santorini is one of the most romantic ___ for couples.' (destination)",
          a: "destinations",
          accept: { ignoreCase: true, alts: ["places", "islands"] },
          successtip: "✓ PLURAL REQUIRED!\n'One of the most romantic destinations' = plural! Greek island paradise! 🇬🇷💙"
        },
        {
          type: "mc",
          q: "Blog: 'The Grand Canyon is ___ one of the most impressive natural wonders.'",
          options: [
            "easily",
            "hardly",
            "barely"
          ],
          a: "easily",
          successtip: "✓ NATURAL MARVEL!\n'Easily one of...' = combining emphasis + 'one of'! Advanced structure! 🏞️🇺🇸"
        },
        {
          type: "order",
          q: "Traduce: Dubai es mucho más moderna que muchas otras ciudades",
          a: "Dubai is much more modern than many other cities",
          successtip: "✓ MODERN METROPOLIS!\n'Much more modern' = Dubai built recently, ultra-contemporary! 🇦🇪🏙️"
        },
        {
          type: "mc",
          q: "Blog: 'London is ___ larger than Edinburgh, but both are beautiful.'",
          options: [
            "much",
            "slightly",
            "a tiny bit"
          ],
          a: "much",
          successtip: "✓ UK SIZE GAP!\nLondon 9M, Edinburgh 500K! 'Much larger' = appropriate! 🇬🇧🏙️"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which sentence shows COMPLETE Unit 8 mastery?",
          options: [
            "'Paris is easily one of the most beautiful cities in Europe. It's much more expensive than Prague but slightly smaller than London. By far the best experience!'",
            "'Paris is easily one of the most beautiful city in Europe. It's slightly more expensive than Prague but much smaller than London.'",
            "'Paris is one of beautiful city. It more expensive than Prague and smaller than London.'"
          ],
          a: "'Paris is easily one of the most beautiful cities in Europe. It's much more expensive than Prague but slightly smaller than London. By far the best experience!'",
          successtip: "🎉 UNIT 8 COMPLETELY MASTERED! 🎉\n\n✅ COMPARATIVE MODIFIERS\n• BIG differences: much, a lot ✅\n  'Much more expensive', 'a lot bigger' ✅\n• SMALL differences: slightly, a bit, a little ✅\n  'Slightly warmer', 'a bit older' ✅\n\n✅ SUPERLATIVE MODIFIERS\n• EASILY (sin duda): 'easily the tallest' ✅\n• BY FAR (de lejos): 'by far the best' ✅\n• BY FAR = strongest emphasis! ✅\n\n✅ 'ONE OF' STRUCTURE (CRITICAL!)\n• ONE OF + THE + SUPERLATIVE + PLURAL NOUN ✅\n• 'One of the most beautiful cities' (PLURAL!) ✅\n• NEVER singular: 'one of the best city' ❌\n• 'One of the tallest buildings' ✅\n\n✅ CORRECT IN FINAL SENTENCE:\n• 'Easily one of the most beautiful cities' ✅\n• 'Much more expensive' (big difference) ✅\n• 'Slightly smaller' (small difference) ✅\n• 'By far the best' (strongest emphasis) ✅\n• All nouns correctly plural after 'one of' ✅\n\n✅ GEOGRAPHIC VOCABULARY\n• Mountains, rivers, deserts, oceans ✅\n• Countries, cities, continents ✅\n• Natural wonders, landmarks ✅\n\n🌟 YOU CAN NOW:\n• Show precise degrees of difference ✅\n• Emphasize superlatives naturally ✅\n• Use 'one of' structure correctly (plural!) ✅\n• Compare places like a native speaker ✅\n• Sound sophisticated and accurate ✅\n\nB1-B2 comparison modifiers = MASTERED! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - COMPARING WORLD CITIES (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u8-reading",
    title: "Reading Practice - Comparing World Cities",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Similar European Cities (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Comparing World Cities**

**Part 1: Barcelona vs Madrid**

I've visited both Barcelona and Madrid. They're wonderful Spanish cities but slightly different. Barcelona is a bit warmer because it's coastal. Madrid is slightly larger in population. Both have amazing food, though Barcelona is a little more touristy.`
      },
      {
        type: "mc",
        q: "What modifiers are used for small differences?",
        options: [
          "Slightly, a bit, a little",
          "Much, a lot",
          "By far"
        ],
        a: "Slightly, a bit, a little",
        successtip: "✓ SMALL DIFFERENCES!\nAll three modifiers = minor differences between similar cities! 📐"
      },
      {
        type: "blank",
        q: "Why are 'slightly', 'a bit', 'a little' used here?",
        a: "small differences",
        accept: { ignoreCase: true, alts: ["small", "minor differences", "minor", "similar cities"] },
        successtip: "✓ APPROPRIATE CHOICE!\nBarcelona and Madrid = similar cities, so small difference modifiers perfect! 🇪🇸"
      },

      // ======================================================================
      // BLOQUE 2: Dramatically Different Cities (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 2: Dubai vs London**

Dubai and London are dramatically different. Dubai is much hotter than London in summer - often reaching 45°C! Dubai is also a lot more modern, with skyscrapers everywhere. London is much older and more historic. Both amazing but completely different experiences!`
      },
      {
        type: "mc",
        q: "What modifiers show BIG differences?",
        options: [
          "Much, a lot",
          "Slightly, a bit",
          "Easily"
        ],
        a: "Much, a lot",
        successtip: "✓ BIG DIFFERENCES!\n'Much' and 'a lot' = significant differences! Dubai vs London = very different! 🌡️🏙️"
      },
      {
        type: "mc",
        q: "Why 'much hotter' instead of 'slightly hotter'?",
        options: [
          "Because temperature difference is huge (45°C vs 20°C)",
          "Just random choice",
          "They mean the same"
        ],
        a: "Because temperature difference is huge (45°C vs 20°C)",
        successtip: "✓ APPROPRIATE MODIFIER!\n'Much' = correct for dramatic 25°C difference! Precision matters! 🌡️"
      },

      // ======================================================================
      // BLOQUE 3: World Records (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 3: World Records**

Mount Everest is easily the tallest mountain on Earth at 8,849 meters. The Pacific Ocean is by far the largest ocean, covering more area than all land combined. These records are undeniable. Russia is by far the largest country by area.`
      },
      {
        type: "mc",
        q: "What two superlative modifiers appear?",
        options: [
          "Easily and by far",
          "Much and a lot",
          "Slightly and a bit"
        ],
        a: "Easily and by far",
        successtip: "✓ EMPHASIS WORDS!\n'Easily' and 'by far' = emphasize superlatives! No doubt about these records! 🏆"
      },
      {
        type: "mc",
        q: "Which is STRONGER emphasis?",
        options: [
          "'By far' is strongest",
          "'Easily' is strongest",
          "They're exactly the same"
        ],
        a: "'By far' is strongest",
        successtip: "✓ EMPHASIS SCALE!\n'Easily' = strong. 'By far' = STRONGEST! Huge margin emphasized! 💪"
      },

      // ======================================================================
      // BLOQUE 4: Travel Highlights (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 4: My Favorite Places**

Iceland is one of the most beautiful countries I've visited. Reykjavik is one of the cleanest cities I've seen. The Northern Lights are one of the most magical experiences. Japan has one of the most efficient transportation systems globally.`
      },
      {
        type: "mc",
        q: "The structure 'one of the most beautiful...' requires what?",
        options: [
          "PLURAL noun (countries, cities, experiences)",
          "Singular noun",
          "No noun"
        ],
        a: "PLURAL noun (countries, cities, experiences)",
        successtip: "✓ CRITICAL RULE!\n'One of' + superlative + PLURAL! All examples correct: countries, cities, experiences! ✅"
      },
      {
        type: "blank",
        q: "Error check: Are all nouns correctly plural in this passage?",
        a: "yes",
        accept: { ignoreCase: true, alts: ["yes they are", "correct", "all correct"] },
        successtip: "✓ ALL CORRECT!\nCountries, cities, experiences, systems = all plural! Perfect usage! 💯"
      },

      // ======================================================================
      // BLOQUE 5: Personal Rankings + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 5: Final Thoughts**

After traveling extensively, Tokyo is easily one of the most organized cities. Paris is by far the most romantic. New York is much more diverse than most cities. Rome is slightly more touristy than Milan but both are incredible!`
      },
      {
        type: "mc",
        q: "How many different modifiers are used in this passage?",
        options: [
          "Five: easily, by far, much, slightly, one of",
          "Only two",
          "None"
        ],
        a: "Five: easily, by far, much, slightly, one of",
        successtip: "✓ ALL MODIFIERS!\nAll Unit 8 modifiers in one passage! Natural integration! 🌟"
      },
      {
        type: "mc",
        q: "'Tokyo is easily one of the most organized cities' - is this correct?",
        options: [
          "Yes - 'easily' emphasizes, 'cities' is correctly plural",
          "No - should be 'city' (singular)",
          "No - can't use 'easily' with 'one of'"
        ],
        a: "Yes - 'easily' emphasizes, 'cities' is correctly plural",
        successtip: "✓ ADVANCED STRUCTURE!\n'Easily' + 'one of' + plural = combining emphasis with 'one of'! Master level! 🎯"
      },
      {
        type: "mc",
        q: "Throughout this entire reading, what grammar was practiced?",
        options: [
          "All comparison modifiers: much/a lot, slightly/a bit, easily, by far, one of + plural",
          "Only basic comparatives",
          "No special grammar"
        ],
        a: "All comparison modifiers: much/a lot, slightly/a bit, easily, by far, one of + plural",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ SMALL DIFFERENCE MODIFIERS\n• Slightly warmer, a bit larger, a little more touristy ✅\n• Used for similar cities (Barcelona/Madrid) ✅\n\n✅ BIG DIFFERENCE MODIFIERS\n• Much hotter, a lot more modern, much older ✅\n• Used for very different cities (Dubai/London) ✅\n\n✅ SUPERLATIVE EMPHASIS\n• Easily the tallest, by far the largest ✅\n• 'By far' = strongest emphasis! ✅\n\n✅ 'ONE OF' STRUCTURE\n• One of the most beautiful countries (PLURAL!) ✅\n• One of the cleanest cities ✅\n• One of the most magical experiences ✅\n• All nouns correctly plural! ✅\n\n✅ ADVANCED COMBINATION\n• 'Easily one of the most organized cities' ✅\n• Combining modifiers naturally! ✅\n\n✅ NATURAL USAGE\n• All modifiers in real travel context ✅\n• Precise, sophisticated comparisons ✅\n\n🌟 UNIT 8 IN ACTION!\nModifiers + One of structure = Native-level comparisons! 💪📖"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 8 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u8-review",
    title: "⭐ Unit 8 Review: Comparing Places",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "For BIG differences, we use:",
        options: [
          "Much or a lot (much bigger)",
          "Slightly or a bit",
          "By far"
        ],
        a: "Much or a lot (much bigger)",
        successtip: "✓ BIG DIFFERENCE!\n'Much/a lot' = significant differences. 'Dubai is much hotter than London'! 🌡️📏"
      },
      
      {
        type: "blank",
        q: "For SMALL differences, we use: ___, ___, or ___. (three modifiers)",
        a: "slightly, a bit, a little",
        accept: { ignoreCase: true, alts: ["slightly / a bit / a little", "slightly, a bit, a little", "a bit, slightly, a little"] },
        successtip: "✓ SMALL DIFFERENCES!\nAll three = minor differences. 'Barcelona is slightly warmer than Madrid'! 📐"
      },
      
      {
        type: "mc",
        q: "Which is CORRECT?",
        options: [
          "Paris is one of the most beautiful cities (plural!)",
          "Paris is one of the most beautiful city",
          "Both are correct"
        ],
        a: "Paris is one of the most beautiful cities (plural!)",
        successtip: "✓ CRITICAL RULE!\n'One of' + superlative + PLURAL! Most common error! 'Cities' not 'city'! ⚠️✅"
      },
      
      {
        type: "order",
        q: "Traduce: El Monte Everest es fácilmente la montaña más alta",
        a: "Mount Everest is easily the tallest mountain",
        successtip: "✓ SUPERLATIVE EMPHASIS!\n'Easily the tallest' = without doubt! Emphasizes superlative! 🏔️✨"
      },
      
      {
        type: "mc",
        q: "Which is the STRONGEST emphasis?",
        options: [
          "By far (strongest!)",
          "Easily",
          "Much"
        ],
        a: "By far (strongest!)",
        successtip: "✓ MAXIMUM EMPHASIS!\n'By far' = de lejos, por mucho. Strongest possible! 'By far the best'! 💪🏆"
      },
      
      {
        type: "blank",
        q: "Fix error: 'One of the best restaurant' → 'One of the best ___'",
        a: "restaurants",
        accept: { ignoreCase: true },
        successtip: "✓ ERROR CORRECTED!\n'Restaurants' (plural!) = must be plural after 'one of'! Common mistake! ✅"
      },
      
      {
        type: "mc",
        q: "'Tokyo is ___ more expensive than Bangkok.' (significant difference)",
        options: [
          "much / a lot",
          "slightly",
          "a bit"
        ],
        a: "much / a lot",
        successtip: "✓ BIG COST GAP!\n'Much more expensive' = Tokyo $$$ vs Bangkok $! Appropriate modifier! 💰"
      },
      
      {
        type: "order",
        q: "Traduce: Rusia es por lejos el país más grande por área",
        a: "Russia is by far the largest country by area",
        successtip: "✓ LAND GIANT!\n'By far the largest' = Russia 17M km²! Huge margin! Strongest emphasis! 🇷🇺🌍"
      },
      
      {
        type: "mc",
        q: "Position of 'easily' and 'by far':",
        options: [
          "Before 'the' (easily the best, by far the tallest)",
          "After the adjective",
          "At end of sentence"
        ],
        a: "Before 'the' (easily the best, by far the tallest)",
        successtip: "✓ WORD ORDER!\n'Easily/by far + THE + superlative' = fixed position! 📍"
      },
      
      {
        type: "mc",
        q: "FINAL QUESTION: What is the MOST CRITICAL rule in Unit 8?",
        options: [
          "After 'one of the' + superlative, noun MUST be PLURAL (cities, not city)",
          "Always use 'much' never 'a lot'",
          "'Easily' is stronger than 'by far'"
        ],
        a: "After 'one of the' + superlative, noun MUST be PLURAL (cities, not city)",
        successtip: "🎉 UNIT 8 COMPLETELY MASTERED! 🎉\n\n✅ COMPARATIVE MODIFIERS\n• BIG differences: much, a lot ✅\n  'Much bigger', 'a lot more expensive' ✅\n• SMALL differences: slightly, a bit, a little ✅\n  'Slightly warmer', 'a bit older' ✅\n• Choose based on actual difference size! ✅\n\n✅ SUPERLATIVE MODIFIERS\n• EASILY: sin duda, fácilmente ✅\n  'Easily the tallest' = without doubt ✅\n• BY FAR: de lejos, por mucho ✅\n  'By far the best' = STRONGEST emphasis ✅\n• Both go BEFORE 'the' ✅\n\n✅ 'ONE OF' STRUCTURE (CRITICAL!) ⚠️\n• ONE OF + THE + SUPERLATIVE + PLURAL NOUN ✅\n• 'One of the most beautiful cities' (PLURAL!) ✅\n• NEVER singular: 'one of the best city' ❌\n• Why? One example from a GROUP (plural) ✅\n• Most common error at all levels! ✅\n\n✅ COMBINATIONS\n• 'Easily one of the tallest buildings' ✅\n• Can combine 'easily'/'by far' with 'one of' ✅\n• Advanced native-level structure! ✅\n\n✅ PRECISION\n• Small difference = slightly/a bit ✅\n• Big difference = much/a lot ✅\n• No doubt = easily ✅\n• Huge margin = by far ✅\n• Makes comparisons sophisticated! ✅\n\n🌟 YOU CAN NOW:\n• Show exact degrees of difference ✅\n• Emphasize superlatives naturally ✅\n• Use 'one of' structure perfectly (plural!) ✅\n• Compare places with native precision ✅\n• Sound sophisticated and accurate ✅\n\nB1-B2 comparison modifiers = MASTERED! 💪\n\n¡Excelente trabajo! Ready for Unit 9! 🚀"
      }
    ]
  }
};
